import { firstValueFrom, map } from "rxjs";

// eslint-disable-next-line no-restricted-imports
import { KdfConfig, KdfConfigService, KeyService } from "@bitwarden/key-management";

import { assertNonNullish } from "../../auth/utils";
import { KdfRequest } from "../../models/request/kdf.request";
import { SdkService } from "../../platform/abstractions/sdk/sdk.service";
import { UserId } from "../../types/guid";
import { EncString } from "../crypto/models/enc-string";
import { InternalMasterPasswordServiceAbstraction } from "../master-password/abstractions/master-password.service.abstraction";
import {
  fromSdkAuthenticationData,
  MasterPasswordAuthenticationData,
  MasterPasswordUnlockData,
} from "../master-password/types/master-password.types";

import { ChangeKdfApiService } from "./change-kdf-api.service.abstraction";
import { ChangeKdfService } from "./change-kdf.service.abstraction";

export class DefaultChangeKdfService implements ChangeKdfService {
  constructor(
    private changeKdfApiService: ChangeKdfApiService,
    private sdkService: SdkService,
    private keyService: KeyService,
    private masterPasswordService: InternalMasterPasswordServiceAbstraction,
    private kdfConfigService: KdfConfigService,
  ) {}

  async updateUserKdfParams(masterPassword: string, kdf: KdfConfig, userId: UserId): Promise<void> {
    assertNonNullish(masterPassword, "masterPassword");
    assertNonNullish(kdf, "kdf");
    assertNonNullish(userId, "userId");
    const updateKdfResult = await firstValueFrom(
      this.sdkService.userClient$(userId).pipe(
        map(async (sdk) => {
          if (!sdk) {
            throw new Error("SDK not available");
          }

          using ref = sdk.take();

          const updateKdfResponse = ref.value
            .crypto()
            .make_update_kdf(masterPassword, kdf.toSdkConfig());
          return await updateKdfResponse;
        }),
      ),
    );

    const authenticationData: MasterPasswordAuthenticationData = fromSdkAuthenticationData(
      updateKdfResult.masterPasswordAuthenticationData,
    );
    const unlockData: MasterPasswordUnlockData = MasterPasswordUnlockData.fromSdk(
      updateKdfResult.masterPasswordUnlockData,
    );
    const oldAuthenticationData: MasterPasswordAuthenticationData = fromSdkAuthenticationData(
      updateKdfResult.oldMasterPasswordAuthenticationData,
    );

    const request = new KdfRequest(authenticationData, unlockData);
    request.authenticateWith(oldAuthenticationData);
    await this.changeKdfApiService.updateUserKdfParams(request);

    // Update the locally stored master key and hash, so that UV, etc. still works
    const masterKey = await this.keyService.makeMasterKey(
      masterPassword,
      unlockData.salt,
      unlockData.kdf,
    );
    await this.masterPasswordService.setMasterKey(masterKey, userId);
    await this.masterPasswordService.setMasterPasswordUnlockData(unlockData, userId);
    await this.masterPasswordService.setMasterKeyEncryptedUserKey(
      new EncString(unlockData.masterKeyWrappedUserKey),
      userId,
    );
    await this.kdfConfigService.setKdfConfig(userId, kdf);
  }
}
